import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, forkJoin, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AppProperties, MeldingType, StatusOptions } from '@models';

import { EcertMeldingService } from '@util-services/ecert-melding.service';

import { Context } from '@config';
import { isEmptyValue } from '@helpers';
import { ApiSession } from './api-session';
import { HttpWrapperOptionsType } from './http-wrapper-options-type';
import { DialogContent, HttpWrapperDialogs } from './http-wrapper.dialogs';
import { HttpWrapper } from './http-wrapper.interface';

// export const ContextRoot = '/' + Context.root;
export const ContextRoot = Context.root.replace('/api','');


@Injectable()
export class HttpWrapperService implements OnDestroy, HttpWrapper {
  private appPropertiesSubject = new ReplaySubject<AppProperties>(null);
  appProperties$ = this.appPropertiesSubject.asObservable();

  private stopCommunication = false;

  private dialogs: HttpWrapperDialogs = null;
  private apiSession: ApiSession = null;

  private onInitTimeout = null;
  private subscriptions: Subscription[] = [];

  private static readonly ContentType = 'Content-Type';
  private static readonly DefaultContentType = 'application/json; charset=utf-8';

  constructor(
    private http: HttpClient,
    dialog: MatDialog,
    eCertMeldingService: EcertMeldingService,
  ) {
    this.dialogs = new HttpWrapperDialogs(eCertMeldingService);
    this.apiSession = new ApiSession(dialog, this, ContextRoot);

    this.onInitTimeout = setTimeout(() => this.onInit(), 1);
  }

  private onInit() {
    this.apiSession.onInit();
/*
    this.subscriptions.push(
      this.post(ContextRoot + '/getApplicationProperties', null).subscribe((appProperties: AppProperties) => {
        this.apiSession.setSessieTimeout(appProperties.appTimeout);
        this.appPropertiesSubject.next(appProperties);
      })
    );
*/
    this.onInitTimeout = null;
  }

  ngOnDestroy(): void {
    if (this.onInitTimeout) {
      clearTimeout(this.onInitTimeout);
    }
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.apiSession.onDestroy();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<TBody = any, TResponse = any>(
    url: string,
    body: TBody,
    options?: HttpWrapperOptionsType,
    showError?: boolean,
    isPopup?: boolean,
    bestemming?: string
  ): Observable<TResponse> {
    this.apiSession.restartSessieTimeout();

    if (isEmptyValue(options)) {
      options = {};
    }
    if (!isEmptyValue(options.headers) && options.headers.keys().indexOf(HttpWrapperService.ContentType) == -1) {
      options.headers.append(HttpWrapperService.ContentType, HttpWrapperService.DefaultContentType);
    } else {
      options.headers = new HttpHeaders({ [HttpWrapperService.ContentType]: HttpWrapperService.DefaultContentType });
    }

    const subject = new Subject<TResponse>();
    this.subscriptions.push(
      this.http
        .post<TResponse>(url, body, options)
        .pipe(catchError((err: HttpErrorResponse) => this.catchError(err, subject, showError, isPopup, bestemming)))
        .subscribe((response: TResponse) => subject.next(response))
    );
    return subject.asObservable();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put<TBody = any, TResponse = any>(
    url: string,
    body: TBody,
    showError?: boolean,
    isPopup?: boolean,
    bestemming?: string
  ): Observable<TResponse> {
    this.apiSession.restartSessieTimeout();
    const subject: Subject<TResponse> = new Subject<TResponse>();

    const options = { headers: new HttpHeaders({ [HttpWrapperService.ContentType]: HttpWrapperService.DefaultContentType }) };
    this.subscriptions.push(
      this.http
        .post<TResponse>(url, body, options)
        .pipe(catchError((err: HttpErrorResponse) => this.catchError(err, subject, showError, isPopup, bestemming)))
        .subscribe((response: TResponse) => subject.next(response))
    );
    return subject.asObservable();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  postJSON<TBody = any, TResponse = any>(url: string, body: TBody, options?: HttpWrapperOptionsType): Observable<TResponse> {
    if (typeof options !== 'undefined') {
      options.headers.append('Content-Type', 'application/json; charset=utf-8');
    } else {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json; charset=utf-8' });
      options = { headers: headers };
    }
    return this.post<TBody, TResponse>(url, body, options);
    // TODO moet een error catcher krijgen:
    //   .pipe(catchError((err: HttpErrorResponse) => this.catchError(err)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  postWithStatusUpdate<TBody = any, TResponse = any>(
    url: string,
    body: TBody,
    options: HttpWrapperOptionsType = {},
    statusOptions: StatusOptions = null
  ): Observable<TResponse> {
    this.apiSession.restartSessieTimeout();
    const subject: Subject<TResponse> = new Subject<TResponse>();
    const content = { content: statusOptions, state: MeldingType.WAITING };

    if (isEmptyValue(statusOptions) || isEmptyValue(statusOptions.showBusy) || statusOptions.showBusy) {
      this.createErrorDialog(content);
    }
    const sub = this.http
      .post<TResponse>(url, body, options)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          // TODO catchError moet gegeneraliseerde versie gebruiken
          let response = { ...err };
          if (err.status == 417) {
            if (!this.stopCommunication) {
              this.stopCommunication = true;
              statusOptions.errorText = err.statusText;
              response = { ...response, message: err.statusText };
              this.apiSession.verkeerdeSectorDialoog();
            }
          }
          content.state = MeldingType.ERROR;
          statusOptions.errorText = statusOptions.errorText + '\n' + response.message;
          if (!statusOptions.showBusy) {
            this.createErrorDialog(content);
          }
          subject.error(response);

          return EMPTY;
          // return caught;
        })
      )
      .subscribe((response: TResponse) => {
        content.state = MeldingType.SUCCES;
        if (!statusOptions.showBusy && (statusOptions.showSuccess || typeof statusOptions.showSuccess == 'undefined')) {
          this.createErrorDialog(content);
        }

        subject.next(response);
      });
    this.subscriptions.push(sub);

    return subject.asObservable();
  }

  //

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get<TQuery = Object | null, TResponse = any>(
    url: string,
    query: TQuery = null,
    options: HttpWrapperOptionsType = null,
    errorResponse: TResponse = null
  ): Observable<TResponse> {
    this.apiSession.restartSessieTimeout();

    let queryString = '';
    if (!isEmptyValue(query) && Object.keys(query).length) {
      queryString =
        '?' +
        Object.keys(query)
          .map((key: string) => `${key}=${query[key]}`)
          .join('&');
    }

    if (isEmptyValue(options)) {
      options = {};
    }
    if (!isEmptyValue(options.headers) && options.headers.keys().indexOf(HttpWrapperService.ContentType) == -1) {
      options.headers.append(HttpWrapperService.ContentType, HttpWrapperService.DefaultContentType);
    } else {
      options.headers = new HttpHeaders({ [HttpWrapperService.ContentType]: HttpWrapperService.DefaultContentType });
    }

    return this.http.get<TResponse>(url + queryString).pipe(
      catchError((err: HttpErrorResponse) => {
        const statusOptions = {} as StatusOptions;
        const response = { message: '' };

        if (err.status == 403) {
          statusOptions.errorText = err.statusText;
          response.message = err.statusText;
          this.apiSession.gebruikerIsUitgelogd();
        } else if (err.status == 417) {
          if (!this.stopCommunication) {
            this.stopCommunication = true;
            statusOptions.errorText = err.statusText;
            response.message = err.statusText;
            this.apiSession.verkeerdeSectorDialoog();
          }
        }
        return isEmptyValue(errorResponse) ? EMPTY : of(errorResponse);
      })
    );
  }

  delete<TResponse = void | string>(url: string): Observable<TResponse> {
    this.apiSession.restartSessieTimeout();

    return this.http.delete<TResponse>(url).pipe(catchError((err: HttpErrorResponse) => this.catchError(err)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  wachtOpMeerdereRequests(requests: Observable<any>[]): Observable<void> {
    const completedSubject = new Subject<void>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const observablesList: { request: any; subject: Subject<boolean> }[] = [];
    const observables = [];

    for (const request of requests) {
      // Aan elke request wordt een Subject gekoppeld.
      // De observable van de Subject wordt aan de array observables toegevoegd.
      const subject = new Subject<boolean>();
      observablesList.push({ request: request, subject: subject });
      observables.push(subject.asObservable());
    }

    for (let i = 0; i < requests.length; i++) {
      // Als de request een response geeft, dan worden .next() en .complete() aangeroepen op de bijbehorende subject.
      const request = observablesList[i].request;
      const subject = observablesList[i].subject;

      this.subscriptions.push(
        request
          .pipe(
            catchError((err: HttpErrorResponse) => {
              subject.error(err);
              return EMPTY;
            })
          )
          .subscribe(() => {
            subject.next();
            subject.complete();
          })
      );
    }

    // Als alle subjects completed zijn, dan wordt de subscribe van de forkJoin aangeroepen.
    this.subscriptions.push(
      forkJoin(observables)
        .pipe(
          catchError((err: HttpErrorResponse) => {
            completedSubject.error(err);
            return EMPTY;
          })
        )
        .subscribe(() => {
          completedSubject.next();
        })
    );

    return completedSubject.asObservable();
  }

  createErrorDialog(content: DialogContent, bestemming?: string): void {
    this.dialogs.createErrorDialog(content, bestemming);
  }
  customError(melding: string, isPopup?: boolean, bestemming?: string): void {
    this.dialogs.customError(melding, isPopup, bestemming);
  }

  customErrorWithCustomHeader(header: string, melding: string, isPopup?: boolean, bestemming?: string): void {
    this.dialogs.customErrorWithCustomHeader(header, melding, isPopup, bestemming);
  }

  customSuccess(melding: string, isPopup?: boolean, bestemming?: string): void {
    this.dialogs.customSuccess(melding, isPopup, bestemming);
  }

  removeError(): void {
    this.dialogs.removeError();
  }

  customErrorMetDocument(header: string, melding: string, isPopup?: boolean, bestemming?: string, bestand?: string): void {
    this.dialogs.customErrorMetBestand(header, melding, isPopup, bestemming, bestand);
  }

  uitloggen(urlBestemming: string = null): void {
    this.apiSession.uitloggen(urlBestemming);
  }

  private catchError<TResponse>(
    err: HttpErrorResponse,
    subject?: Subject<TResponse>,
    showError?: boolean,
    isPopup?: boolean,
    bestemming?: string
  ): Observable<never> {
    const statusOptions = {} as StatusOptions;
    const content = { content: statusOptions, state: MeldingType.ERROR, isPopup };

    let response = { message: '' };

    if (err.status == 403) {
      statusOptions.errorText = err.statusText;
      response.message = err.statusText;
      this.apiSession.gebruikerIsUitgelogd();
    } else if (err.status == 417) {
      if (!this.stopCommunication) {
        this.stopCommunication = true;
        statusOptions.errorText = err.statusText;
        response.message = err.statusText;
        this.apiSession.verkeerdeSectorDialoog();
      }
    } else {
      if (err.status == 500) {
        statusOptions.errorText = err.statusText;
        response.message = err.statusText;
      } else {
        response = err.error;
        // Als de error in de response van het type Text (string) is, dan deze parsen naar JSON-formaat.
        if (typeof err.error == 'string') {
          try {
            response = JSON.parse(err.error);
          } catch (e) {
            // Als de error niet geparsed kan worden naar JSON:
            //   geen probleem, de response zal dan de string zelf bevatten
          }
        }
        content.state = MeldingType.ERROR;
        statusOptions.errorText = response ? response.message : 'onbekende fout';
      }
      if (isEmptyValue(showError) || showError) {
        this.createErrorDialog(content, bestemming);
      }
    }

    if (!isEmptyValue(subject)) {
      subject.error(response);
    } else {
      throw err;
    }

    return EMPTY;
  }
}
